// src/encounter/vitals-edit-dialog.tsx
// Edit dialog for the pre-encounter vitals (PROD-1245). PRESENTATIONAL — composes DS Modal +
// VitalIcon/Icon atoms into the Figma "Edit Vitals" tile language (VHvlfNWyFEymdjNpb6x45i
// 613:2118, parity loop 2): each editable vital renders as an icon tile with an INLINE value
// field + unit + label and a per-tile confirm check that lights once the field holds a valid
// number — instead of the previous plain TextField form. The DS VitalsRow is a display template
// (onEdit callback, no edit UI), so editing stays composed app-side from DS atoms + tokens.
// Figma legacy palette → DS tokens (signal-ds governance, never raw hex):
//   inline field bg   Figma "Light Grey"                → --color-surface-background
//   confirm check     Figma "Scrips Primary Light Blue" → --color-brand-primary
// No hooks/network: the host supplies `existing` (pre-fill) + submit/close; on submit it emits a
// VitalsFormValue the rail turns into a wire body via buildVitalsRequest + useSaveVitals.
// Validation (BP both-or-neither, numeric) is shared with the builder via validateVitalsForm.
import { useMemo, useState, type ReactNode } from 'react';
import {
  Modal,
  Banner,
  Button,
  ObservationIconSeat,
  HearingTestEditor,
  parseHearingValue,
  combineHearingValue,
  type VitalKind,
  type VitalsRowPatientType,
} from '@scripsteam/scrips-signal-ds';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { VITAL_KIND_TO_GLYPH } from '@/encounter/lib/vital-glyph';
import type { PatientVital } from '@/types/vitals-api';
import {
  computeBmi,
  validateVitalsForm,
  vitalsToFormValue,
  type VitalsFormValue,
  type LmpFlags,
} from '@/encounter/lib/to-vitals-request';
import { encounterVitalsFor, type EncounterVital } from '@/encounter/lib/encounter-vitals';
import { waistToHipFrom } from '@/encounter/lib/to-vitals-row';
import { LmpEditor } from '@/scheduling/components/view-appointment-panel/vitals/lmp-editor';
import { lmpIsMenstrualFrom } from '@/scheduling/components/view-appointment-panel/vitals/lmp-flags';
import { VITAL_CODES } from '@/types/vitals-api';

export interface VitalsEditDialogProps {
  open: boolean;
  /** Current readings → pre-fill (edit) vs empty (first capture). */
  existing?: PatientVital[] | null;
  saving?: boolean;
  /** `lmpFlags`: the LMP row's menstruating / breast-feeding flags (real wire fields), sent with the LMP. */
  onSubmit: (value: VitalsFormValue, lmpFlags?: LmpFlags) => void;
  /**
   * PROD-3130: which vitals the form draws — the encounter's ONE vitals list (encounter-vitals.ts) for this patient,
   * the same list and names as the PE vitals cards. Absent: the list without the context vitals (LMP, head
   * circumference).
   */
  patientType?: VitalsRowPatientType;
  onClose: () => void;
  /**
   * WAVE 2 (Samer 2026-07-23): visual acuity is PART of the vitals — the standalone
   * "+ Visual Acuity" header link is retired; the capture affordance lives in the Edit
   * flow. When wired, a "Visual Acuity" tile renders in the grid; clicking it hands off
   * to the acuity dialog (the host closes this dialog and opens that one).
   */
  onVisualAcuity?: () => void;
  /** Current acuity summary (e.g. "20/40 · 20/25") — shown on the tile; absent → "Add". */
  visualAcuitySummary?: string | null;
}

// ---- Figma 613:2118 tile anatomy, DS VitalCard geometry (radius 14, border-default) ----
const GRID = 'grid grid-cols-3 gap-3';
// Radius 14 = the DS --radius-tile token (the VitalCard tile radius).
const TILE =
  'flex items-center gap-2 min-h-14 pt-1 pr-3 pb-1 pl-2 ' +
  'border border-solid border-border-default rounded-tile bg-surface-card';
// 6px radius = the DS --radius-control token.
const VALUE_INPUT =
  'w-13 py-0.75 px-2 text-size-body-15 leading-compact font-family-inherit ' +
  'text-text-primary bg-surface-background ' + // Figma "Light Grey" inline field
  'border border-solid border-transparent rounded-control';
const UNIT = 'text-size-sm text-text-muted whitespace-nowrap';
// 21-10 design: the confirm check is a 20 circle (hairline; brand fill and a white check once the value is valid). Its colours are the
// Objective sheet's (`[data-part="check"]`, encounter-objective.css); the shape is `rounded-full`, the DS radius.
const CHECK = 'inline-flex size-5 shrink-0 items-center justify-center rounded-full border border-solid border-border-default text-transparent';
const LABEL = 'text-size-sm text-text-muted leading-compact';

/** Valid captured number → the tile's confirm check lights (Figma per-tile check). */
function isConfirmed(...values: string[]): boolean {
  return values.every((v) => v.trim() !== '' && Number.isFinite(Number(v)));
}

function VitalEntryTile({
  kind,
  label,
  confirmed,
  children,
  qualifier,
  wide = false,
}: {
  kind: VitalKind;
  label: string;
  confirmed: boolean;
  children: ReactNode;
  /** Said after the name, never part of it ("computed"). */
  qualifier?: string;
  /** A tile whose editor needs the row (hearing, LMP). */
  wide?: boolean;
}) {
  return (
    <div className={cn(TILE, wide && 'col-span-3 items-start')} data-vital-kind={kind} data-wide={wide || undefined} data-confirmed={confirmed || undefined}>
      {/* WAVE 2 (Samer 2026-07-23): ONE icon system — the dialog renders the SAME duotone
          identity seat the strip's ObservationTile renders (was the legacy thin-outline
          VitalIcon set: "the icons here in this modal are not the same icons outside").
          Decorative — the input's own aria-label names the field. */}
      {VITAL_KIND_TO_GLYPH[kind] ? (
        <ObservationIconSeat glyph={VITAL_KIND_TO_GLYPH[kind]!} seat={32} icon={18} />
      ) : null}
      <div className="flex flex-col flex-1 min-w-0 gap-y-1">
        <div className="flex items-center gap-2">{children}</div>
        <span className={LABEL} data-part="label">
          <span data-vital-label>{label}</span>
          {qualifier ? ` · ${qualifier}` : null}
        </span>
      </div>
      {/* Confirm check: an empty circle until the value is valid, then brand fill and a white check (design 21-10).
          Always mounted so tiles don't reflow as values are typed; the wide tiles (hearing, LMP) draw none. */}
      {!wide ? (
        <span className={CHECK} data-part="check" aria-hidden>
          {confirmed ? <Check className="size-4" aria-hidden /> : null}
        </span>
      ) : null}
    </div>
  );
}

function hasVitals(existing: PatientVital[] | null | undefined): boolean {
  return (existing ?? []).some((v) => !v.isDeleted);
}

/** Outer gate + keyed remount so each open re-initialises from `existing` via lazy useState. */
export function VitalsEditDialog(props: VitalsEditDialogProps) {
  if (!props.open) return null;
  const key = hasVitals(props.existing) ? 'edit' : 'add';
  return <VitalsEditDialogForm key={key} {...props} />;
}

function VitalsEditDialogForm({ existing = null, saving, onSubmit, onClose, onVisualAcuity, visualAcuitySummary, patientType }: VitalsEditDialogProps) {
  const isEditing = hasVitals(existing);
  const [form, setForm] = useState<VitalsFormValue>(() => vitalsToFormValue(existing));
  const [error, setError] = useState<string | null>(null);
  // The LMP row's flags, seeded from the stored LMP reading through #674's shared helper (the appointment panel and the
  // PE grid read it the same way): a stored "Not menstruating" with no flag is read as not menstruating.
  const lmpRow = (existing ?? []).find((v) => !v.isDeleted && v.code?.code === VITAL_CODES.LMP);
  const [seededLmpFlags] = useState<LmpFlags>(() => ({
    isMenstrual: lmpIsMenstrualFrom(lmpRow),
    isBreastFeeding: lmpRow?.isBreastFeeding ?? false,
  }));
  const [lmpFlags, setLmpFlags] = useState<LmpFlags>(seededLmpFlags);
  // Pregnancy indicator: NO wire field on either platform; form-local, never saved (see LmpEditor).
  const [isPregnant, setIsPregnant] = useState(false);
  const vitals = useMemo(() => encounterVitalsFor(patientType), [patientType]);
  const hasLmp = vitals.some((v) => v.entry.kind === 'lmp');

  const set = (key: keyof VitalsFormValue) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setError(null);
  };

  const bmi = useMemo(() => computeBmi(form.height, form.weight), [form.height, form.weight]);
  const waistToHip = useMemo(
    () => waistToHipFrom(Number(form.waistCirc) || undefined, Number(form.hipCirc) || undefined) ?? '',
    [form.waistCirc, form.hipCirc],
  );

  function handleSubmit() {
    const err = validateVitalsForm(form);
    if (err) {
      setError(err);
      return;
    }
    // The LMP indicators ride on the LMP reading; with no LMP value there is no reading to carry them, so a changed
    // indicator would be lost without a word (Codex P2). Say so instead.
    const flagsChanged =
      lmpFlags.isMenstrual !== seededLmpFlags.isMenstrual || lmpFlags.isBreastFeeding !== seededLmpFlags.isBreastFeeding;
    if (hasLmp && flagsChanged && !form.lmp.trim()) {
      setError('Add the LMP date, or turn on “No longer menstruating”, to save the LMP indicators.');
      return;
    }
    onSubmit(form, hasLmp ? lmpFlags : undefined);
  }

  const field = (
    key: keyof VitalsFormValue,
    ariaLabel: string,
    inputMode: 'decimal' | 'numeric',
    /** BP's two fields are narrower (44px) than the default 52px tile input. */
    widthClass?: string,
  ) => (
    <input
      aria-label={ariaLabel}
      value={form[key]}
      onChange={set(key)}
      inputMode={inputMode}
      disabled={saving}
      className={widthClass ? cn(VALUE_INPUT, widthClass) : VALUE_INPUT}
    />
  );

  function tileFor(v: EncounterVital): ReactNode {
    const { entry } = v;
    switch (entry.kind) {
      case 'number':
        return (
          <VitalEntryTile key={v.key} kind={v.kind} label={v.label} confirmed={isConfirmed(form[entry.field])}>
            {field(entry.field, entry.ariaLabel, entry.inputMode)}
            {entry.unit ? <span className={UNIT} data-part="unit">{entry.unit}</span> : null}
          </VitalEntryTile>
        );
      case 'bp':
        return (
          <VitalEntryTile key={v.key} kind={v.kind} label={v.label} confirmed={isConfirmed(form.systolic, form.diastolic)}>
            {field('systolic', 'Systolic (mmHg)', 'numeric', 'w-12')}
            <span className={UNIT} data-part="unit">/</span>
            {field('diastolic', 'Diastolic (mmHg)', 'numeric', 'w-12')}
          </VitalEntryTile>
        );
      case 'computed': {
        // Computed, never typed; no confirm check (a derived value isn't captured) and no in-range dot (draft values
        // have no backend vitalNormalStatus; we don't recompute clinical thresholds client-side — to-vitals-row rule).
        const value = v.key === 'bmi' ? bmi : waistToHip;
        return (
          <VitalEntryTile key={v.key} kind={v.kind} label={v.label} qualifier="computed" confirmed={false}>
            <span className="text-size-body-15 text-text-primary" data-part="value">{value || '—'}</span>
            {value && v.key === 'bmi' ? <span className={UNIT} data-part="unit">kg/m²</span> : null}
          </VitalEntryTile>
        );
      }
      case 'hearing': {
        const { left, right } = parseHearingValue(form.hearing);
        return (
          <VitalEntryTile key={v.key} kind={v.kind} label={v.label} confirmed={!!form.hearing.trim()} wide>
            {/* The PE inline grid's shape ("bar": SL | L | G over RIGHT and LEFT). Wire "<Left>/<Right>". */}
            <HearingTestEditor
              variant="bar"
              left={left}
              right={right}
              onChange={(l, r) => {
                setForm((f) => ({ ...f, hearing: combineHearingValue(l, r) ?? '' }));
                setError(null);
              }}
              className="w-full"
            />
          </VitalEntryTile>
        );
      }
      case 'lmp':
        return (
          <VitalEntryTile key={v.key} kind={v.kind} label={v.label} confirmed={!!form.lmp.trim()} wide>
            <div className="w-full">
              <LmpEditor
                value={form.lmp}
                isMenstrual={lmpFlags.isMenstrual ?? true}
                onValueChange={(next) => {
                  setForm((f) => ({ ...f, lmp: next }));
                  setError(null);
                }}
                onMenstrualChange={(next) => setLmpFlags((f) => ({ ...f, isMenstrual: next }))}
                showIndicators
                isBreastFeeding={lmpFlags.isBreastFeeding ?? false}
                onBreastFeedingChange={(next) => setLmpFlags((f) => ({ ...f, isBreastFeeding: next }))}
                isPregnant={isPregnant}
                onPregnantChange={setIsPregnant}
              />
            </div>
          </VitalEntryTile>
        );
      case 'acuity':
        // Visual acuity — part of the vitals (Samer wave-2 ruling): a tile-shaped entry into the acuity capture
        // dialog. Without a capture handler it reads as the summary only.
        return onVisualAcuity ? (
          <button
            key={v.key}
            type="button"
            onClick={onVisualAcuity}
            disabled={saving}
            className={cn(TILE, 'cursor-pointer font-inherit text-left')}
            aria-label="Visual acuity — open capture form"
            data-vital-kind={v.kind}
          >
            {VITAL_KIND_TO_GLYPH[v.kind] ? <ObservationIconSeat glyph={VITAL_KIND_TO_GLYPH[v.kind]!} seat={32} icon={18} /> : null}
            <div className="flex flex-col flex-1 min-w-0 gap-y-1">
              {/* Link-blue "Add…" / the current OS/OD summary IS the affordance — no trailing glyph. */}
              <span className={cn('text-size-body-15', visualAcuitySummary ? 'text-text-primary' : 'text-brand-primary')} data-part={visualAcuitySummary ? 'value' : 'link'}>
                {visualAcuitySummary ?? 'Add…'}
              </span>
              <span className={LABEL} data-part="label">
                <span data-vital-label>{v.label}</span>
              </span>
            </div>
          </button>
        ) : (
          <VitalEntryTile key={v.key} kind={v.kind} label={v.label} confirmed={false}>
            <span className="text-size-body-15 text-text-primary" data-part="value">{visualAcuitySummary ?? '—'}</span>
          </VitalEntryTile>
        );
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEditing ? 'Edit vitals' : 'Add vitals'}
      size="lg"
      // PROD-3130 (desk CHANGES on #673, 9 Oct): 16 tiles outgrow the 90vh cap on a 768 px laptop, so the title and
      // the Cancel / Save footer stay pinned and only the tiles scroll.
      scrollBody
      footer={
        <>
          <Button variant="tertiary" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="font-sans" data-slot="vitals-edit-form">
        {/* PROD-3130: the tiles ARE the encounter's vitals list (encounter-vitals.ts), in its order and under its
            names — the same list the PE vitals cards draw. */}
        <div className={GRID}>{vitals.map((v) => tileFor(v))}</div>
        {error ? <Banner variant="error" role="alert" title="Not saved">{error}</Banner> : null}
      </div>
    </Modal>
  );
}
